import API from 'core/api'
import { formatForMillisOffset, parseServerDateTime } from 'core/date-utils'
import { autoRetry, isBlank, sleep } from 'core/utils'
import { chain, findIndex, maxBy, sortBy } from 'lodash'
import { action, computed, observable, runInAction } from 'mobx'
import { Deploy } from 'models/project'
import {
  type BooleanMap,
  type FiltersMap,
  groupTestRunTestsByFile,
  Message,
  type StringMap,
  type TestFile,
  TestListResult,
  TestResult,
  TestRun,
  TestRunAnalysis,
  TestRunnerData,
  TestRunStatus,
  TestRunTest,
  TestRunTestAnalysis,
  TestSelection,
  type TestStatus
} from 'models/test-run'
import { UserFollow } from 'models/user'
import { type AppStoreClass } from './app-store'
import { type ProjectStoreClass } from './project-store'
import { type SettingsStoreClass } from './settings-store'
import { PipelineStore } from './index'

type OpenItemsMap = Record<number, BooleanMap>

export class TestRunStoreClass {
  private readonly AppStore: AppStoreClass
  private readonly ProjectStore: ProjectStoreClass
  private readonly SettingsStore: SettingsStoreClass
  @observable testRuns: TestRun[] = []
  @observable testRunResults: Record<number, TestFile[]> = {}
  @observable testRunTests: Record<number, TestRunTest[]> = {}
  @observable testRunMessages: Record<number, Message[]> = {}
  @observable testRunFollowers: Record<number, UserFollow[]> = {}
  @observable filterDate: Date | null = null
  @observable filterEnvironmentIds: number[] = []
  @observable filterPipelineStageIds: number[] = []
  @observable filterStatus: TestRunStatus[] = []
  @observable filterReviewerIds: number[] = []
  @observable filterProjectId: number | null = null
  @observable filteredTestSuiteId: number | null = null
  @observable filterBranches: string[] = []
  @observable loading: boolean = false
  @observable changingStatus: boolean = false
  @observable testRunsLoaded: boolean = false
  @observable refreshTestRuns: boolean = false
  @observable currentOffset = 0
  @observable statusFiltersByTestRun: FiltersMap = {}
  @observable testRunTabSetting: StringMap = {}
  @observable loadingTestRunResults = false
  @observable openItemsMap: OpenItemsMap = {}
  @observable openTestRunTests: Record<number, boolean> = {}
  @observable testRunComparison1: TestRun | null = null
  @observable testRunComparison2: TestRun | null = null
  refreshTimer: ReturnType<typeof setTimeout> | undefined
  pageSize = 10

  constructor (AppStore: AppStoreClass, ProjectStore: ProjectStoreClass, SettingsStore: SettingsStoreClass) {
    this.AppStore = AppStore
    this.ProjectStore = ProjectStore
    this.SettingsStore = SettingsStore
  }

  @action
  setTestRuns (testRuns: TestRun[]): void {
    this.testRuns = testRuns
  }

  @action
  clear (): void {
    this.testRuns = []
    this.testRunResults = {}
    this.filterDate = null
    this.filterEnvironmentIds = []
    this.filterPipelineStageIds = []
    this.filterBranches = []
    this.filterProjectId = null
    this.filteredTestSuiteId = null
    this.filterStatus = []
    this.filterReviewerIds = []
    this.loading = false
    this.refreshTestRuns = false
    this.changingStatus = false
    this.testRunsLoaded = false
    this.currentOffset = 0
  }

  @action
  async loadTestRun (testRunId: number): Promise<TestRun | undefined> {
    const result = await API.get(`/test-runs/${testRunId}`, TestRun)
    const testRunnerDataResult = await API.getList(`/test-runs/${testRunId}/test-runners`, TestRunnerData)
    if (result.data !== undefined && typeof result.data !== 'string') {
      result.data.testRunners = testRunnerDataResult.data ?? []
      return this.storeTestRun(result.data)
    }
  }

  getTestRun (testRunId: number): TestRun | undefined {
    return this.testRuns.find((t) => t.id === testRunId)
  }

  // eslint-disable-next-line @typescript-eslint/unbound-method
  @action.bound
  async ensureLoaded (refresh: boolean = false): Promise<TestRun[]> {
    this.refreshTestRuns = refresh
    if (!this.testRunsLoaded) {
      await this.loadTestRuns(true)
    }

    return this.testRuns
  }

  // eslint-disable-next-line @typescript-eslint/unbound-method
  @action.bound
  updateFilterDate (value: Date | null): void {
    this.filterDate = value
    void this.loadTestRuns(true)
  }

  // eslint-disable-next-line @typescript-eslint/unbound-method
  @action.bound
  updateFilterPipelineStage (pipelineStageIds: number[]): void {
    this.filterPipelineStageIds = pipelineStageIds
    void this.loadTestRuns(true)
  }

  @action
  setFilterPipelineStage (pipelineStageIds: number[]): void {
    this.filterPipelineStageIds = pipelineStageIds
  }

  // eslint-disable-next-line @typescript-eslint/unbound-method
  @action.bound
  updateFilterEnvironment (environmentIds: number[]): void {
    this.filterEnvironmentIds = environmentIds
    void this.loadTestRuns(true)
  }

  @action
  setFilterEnvironment (environmentIds: number[]): void {
    this.filterEnvironmentIds = environmentIds
  }

  // eslint-disable-next-line @typescript-eslint/unbound-method
  @action.bound
  updateFilterBranch (branches: string[]): void {
    this.filterBranches = branches
    void this.loadTestRuns(true)
  }

  @action
  setFilterBranch (branch: string[]): void {
    this.filterBranches = branch
  }

  // eslint-disable-next-line @typescript-eslint/unbound-method
  @action.bound
  updateFilterTestSuite (testSuiteId: number): void {
    this.filteredTestSuiteId = testSuiteId
    void this.loadTestRuns(true)
  }

  @action
  setFilterTestSuite (testSuiteId: number): void {
    this.filteredTestSuiteId = testSuiteId
  }

  // eslint-disable-next-line @typescript-eslint/unbound-method
  @action.bound
  updateFilterProject (projectId: number): void {
    this.filterProjectId = projectId
    void this.loadTestRuns(true)
  }

  @action
  setFilterProject (projectId: number): void {
    this.filterProjectId = projectId
  }

  // eslint-disable-next-line @typescript-eslint/unbound-method
  @action.bound
  updateFilterStatus (value: TestRunStatus[]): void {
    this.filterStatus = value
    void this.loadTestRuns(true)
  }

  @action
  setFilterStatus (value: TestRunStatus[]): void {
    this.filterStatus = value
  }

  // eslint-disable-next-line @typescript-eslint/unbound-method
  @action.bound
  updateFilterReviewerIds (value: number[]): void {
    this.filterReviewerIds = value
    void this.loadTestRuns(true)
  }

  @action
  setFilterReviewerIds (value: number[]): void {
    this.filterReviewerIds = value
  }

  @action
  updateTestRunComparison1 (testRun: TestRun): void {
    this.testRunComparison1 = testRun
  }

  @action
  updateTestRunComparison2 (testRun: TestRun): void {
    this.testRunComparison2 = testRun
  }

  @action
  async uploadTestRun (testRunUpload: any): Promise<any> {
    const result = await API.postMultipart('/test-runs-upload', testRunUpload, TestRun)

    if (result.status === 0) { throw Error('Test run upload failed') }

    await this.loadTestRuns(true)
    return result.data
  }

  @action
  async uploadTestRunFromSample (testRunUpload: any): Promise<any> {
    const result = await API.post('/test-runs-upload/sample_pytest', testRunUpload, TestRun)

    if (result.status === 0) { throw Error('Test run upload failed') }

    await this.loadTestRuns(true)
    return result.data
  }

  @action
  async createTestRun (testRunRequest: any): Promise<any> {
    const result = await API.post('/test-runs', testRunRequest, TestRun)
    await this.loadTestRuns(true)
    return result.data
  }

  hasRunningTests (testRun: TestRun | undefined): boolean {
    return testRun !== undefined && (testRun.notCompleted || this.getTestRunTests(testRun.id).some((t) => t.notCompleted))
  }

  // eslint-disable-next-line @typescript-eslint/unbound-method
  @action.bound
  async loadTestRuns (resetList = true, offset = 0): Promise<void> {
    if (resetList) {
      this.loading = true
      this.currentOffset = 0
      this.testRuns = []
    }

    const project = this.filterProjectId !== null ? this.ProjectStore.find(this.filterProjectId) : undefined
    const environments = this.SettingsStore.environments.filter((e) => this.filterEnvironmentIds.includes(e.id))
    const pipelineStages = PipelineStore.sortedItems.filter((ps) => this.filterPipelineStageIds.includes(ps.id))
    const testRuns = await autoRetry(async () => {
      const projectKey = project?.key
      const environmentKeys = environments.map(e => e.key)
      const pipelineStageIds = pipelineStages.map(ps => ps.id)
      const testSuiteId = this.filteredTestSuiteId
      const status = this.filterStatus
      const reviewerIds = this.filterReviewerIds
      const date = this.filterDate
      const branches = this.filterBranches

      let url = `/test-runs?offset=${offset > 0 ? offset : 0}&limit=${this.pageSize}`
      if (status.length > 0) url += `&status=${status.join(',')}`
      if (reviewerIds.length > 0) url += `&reviewerIds=${reviewerIds.join(',')}`
      if (environmentKeys.length > 0) url += `&environment=${environmentKeys.join(',')}`
      if (pipelineStageIds.length > 0) url += `&pipelineStage=${pipelineStageIds.join(',')}`
      if (date !== null) url += `&date=${formatForMillisOffset(date)}`
      if (projectKey !== undefined) {
        url += `&project=${projectKey}`
        if (branches.length > 0) url += `&branch=${branches.join(',')}`
      }
      if (testSuiteId !== null && testSuiteId !== 0) url += `&testSuiteId=${testSuiteId}`
      const result = await API.getList(url, TestRun)
      return result.data
    }, 2000)

    runInAction(() => {
      this.testRuns = testRuns ?? []
      this.loading = false
      this.testRunsLoaded = true
      this.scheduleLoadMore()
    })
  }

  // eslint-disable-next-line @typescript-eslint/unbound-method
  @action.bound
  async loadPageChange (page: number): Promise<void> {
    this.loading = true
    this.currentOffset = (page - 1) * this.pageSize
    await this.loadTestRuns(false, this.currentOffset)
  }

  @action
  scheduleLoadMore (): void {
    const hasRunningTests = this.testRuns.some((t) => t.notCompleted)
    const hasSubmittedTestWithNoTests = this.testRuns.some(
      (t) => t.status === TestRunStatus.SUBMITTED && t.totalCount === 0
    )
    const refreshTime = hasSubmittedTestWithNoTests || !this.testRunsLoaded ? 5000 : hasRunningTests ? 10000 : 60000

    if (this.refreshTimer !== undefined) {
      clearTimeout(this.refreshTimer)
    }

    this.refreshTimer = setTimeout(() => {
      if (this.refreshTestRuns) {
        void this.loadTestRuns(false, this.currentOffset)
      } else {
        this.scheduleLoadMore()
      }
    }, refreshTime)
  }

  @action
  async cancelTestRun (testRun: TestRun): Promise<void> {
    await API.patch(`/test-runs/${testRun.id}`, {
      status: TestRunStatus.CANCELED
    })

    await this.loadTestRuns(false)
  }

  @action
  async rerunTestRun (testRun: TestRun): Promise<TestRun | undefined> {
    const newTestRun = await this.callRerun(testRun.id, { rerunTestRun: true })
    if (newTestRun != null) {
      void this.toggleFollow(newTestRun.id)
    }
    await this.loadTestRuns(true)

    return newTestRun
  }

  async callRerun (testRunId: number, data: any): Promise<TestRun | undefined> {
    const result = await API.post(`/test-runs/${testRunId}/rerun`, data, TestRun)
    return result.data
  }

  @action
  async rerunTests (testResults: TestRunTest[]): Promise<TestRun | undefined> {
    if (testResults.length === 0) return

    const testRunId = testResults[0].testRunId

    const newTestRun = await this.callRerun(testRunId, {
      testIds: testResults.map((r) => r.id)
    })

    await this.loadTestRuns(false)

    await this.loadTestRunTests(testRunId, false)

    return newTestRun
  }

  @action
  async rerunFailedTestsSingleRunner (testRunId: number): Promise<TestRun | undefined> {
    const newTestRun = await this.callRerun(testRunId, { rerunFailedTests: true, maxRunners: 1 })

    await this.loadTestRuns(false)

    await this.loadTestRunTests(testRunId, false)

    return newTestRun
  }

  @action
  async rerunFailedTests (testRunId: number): Promise<TestRun | undefined> {
    const newTestRun = await this.callRerun(testRunId, { rerunFailedTests: true })

    await this.loadTestRuns(false)

    await this.loadTestRunTests(testRunId, false)

    return newTestRun
  }

  @action
  async loadTestRunTests (testRunId: number, setLoading = true, loadAll = false): Promise<TestFile[]> {
    this.loadingTestRunResults = setLoading
    const testRun = this.getTestRun(testRunId)
    const currentTests = this.testRunTests[testRunId] ?? []

    let url = `/test-runs/${testRunId}/results`
    const isRefresh = currentTests.length > 0

    if (isRefresh && !loadAll) {
      const max = maxBy(currentTests, 'updatedAt')
      if (max !== undefined) {
        const since = parseServerDateTime(max.updatedAt)?.getTime()
        if (since !== undefined) url += `?since=${since}`
      }
    }

    const results = await API.getList(url, TestRunTest)

    return runInAction(() => {
      const selected = this.getTestRunTests(testRunId).find((t) => t.selected)
      const testsReceived = (results.data ?? []).filter((t) => t.isTestPhase)
      if (isRefresh) {
        testsReceived.forEach((t) => {
          t.selected = t.parentId === selected?.parentId
          const index = findIndex(currentTests, (o) => o.id === t.id)
          if (index === -1) {
            currentTests.push(t)
          } else {
            currentTests[index] = t
          }
        })

        this.testRunTests[testRunId] = currentTests
        if (testRun !== undefined) {
          this.testRunResults[testRunId] = groupTestRunTestsByFile(testRun, currentTests)
        }
      } else {
        testsReceived.forEach((t) => (t.selected = t.parentId === selected?.parentId))
        this.testRunTests[testRunId] = testsReceived
        if (testRun !== undefined) {
          this.testRunResults[testRunId] = groupTestRunTestsByFile(testRun, testsReceived)
        }
      }
      this.loadingTestRunResults = false

      return this.getTestRunResults(testRunId)
    })
  }

  @action
  async createTestRunAnalysis (testRunId: number): Promise<any> {
    const result = await API.post(`test-runs/${testRunId}/analysis`, {}, TestRunAnalysis)
    return result.data
  }

  @action
  async getTestRunAnalysis (testRunId: number): Promise<any> {
    const result = await API.get(`test-runs/${testRunId}/analysis`, TestRunAnalysis)
    return result.data
  }

  @action
  async getTestSelection (testRunId: number): Promise<TestSelection | null> {
    try {
      const result = await API.get(`test-runs/${testRunId}/test-selection`, TestSelection)
      return result.data ?? null
    } catch (error) {
      return null
    }
  }

  @action
  async getSuggestion (testRunId: number, testRunTestId: number): Promise<any> {
    const result = await API.get(`test-runs/${testRunId}/results/${testRunTestId}/analysis`, TestRunTestAnalysis)
    return result.data
  }

  @action
  async createTestRunTestAnalysis (testRunId: number, testRunTestId: number): Promise<any> {
    const result = await API.post(`test-runs/${testRunId}/results/${testRunTestId}/analysis`, TestRunTestAnalysis)
    return result.data
  }

  @action
  async refreshTestRunTests (testRunId: number, since: number): Promise<void> {
    const results = await API.getList(`/test-runs/${testRunId}/results?since=${since}`, TestRunTest)
    const testRun = this.getTestRun(testRunId)
    const updatedTests = results.data ?? []
    if (updatedTests.length > 0) {
      runInAction(() => {
        const selected = this.getTestRunTests(testRunId).find((t) => t.selected)
        const allTests = [...this.testRunTests[testRunId]]
        updatedTests.forEach((t) => {
          t.selected = t.parentId === selected?.parentId
          const index = findIndex(allTests, (o) => o.id === t.id)
          if (index === -1) {
            allTests.push(t)
          } else {
            allTests[index] = t
          }
        })

        this.testRunTests[testRunId] = allTests
        if (testRun !== undefined) {
          this.testRunResults[testRunId] = groupTestRunTestsByFile(testRun, allTests)
        }
      })
    }
  }

  hasTestRunResults (testRunId: number): boolean {
    return this.testRunResults[testRunId] !== undefined
  }

  getTestRunResults (testRunId: number): TestFile[] {
    return this.testRunResults[testRunId] ?? []
  }

  getTestRunTests (testRunId: number): TestRunTest[] {
    return this.testRunTests[testRunId] ?? []
  }

  async getTestResult (testRunId: number, testRunTestId: number): Promise<any> {
    const result = await API.get(`test-runs/${testRunId}/results/${testRunTestId}`, TestResult)
    const testResult = result.data

    // When running by file, all tests in a file share the same runner but screenshots are only
    // attached to the first test's result. Look up sibling tests in the same file and find the
    // one that has screenshots.
    const missingScreenshots = testResult.screenShots == null || testResult.screenShots.length === 0
    if (testResult != null && missingScreenshots && testResult.file !== '') {
      if ((this.testRunTests[testRunId]?.length ?? 0) === 0) {
        await this.loadTestRunTests(testRunId, false)
      }
      const allTests = this.testRunTests[testRunId] ?? []
      const siblingsInSameFile = allTests
        .filter(t => t.projectTest?.testFileName === testResult.file && t.id !== testRunTestId)
        .sort((a, b) => a.id - b.id)
      for (const sibling of siblingsInSameFile) {
        const siblingResult = await API.get(`test-runs/${testRunId}/results/${sibling.id}`, TestResult)
        if (siblingResult.data?.screenShots?.length > 0) {
          testResult.screenShots = siblingResult.data.screenShots
          break
        }
      }
    }

    return testResult
  }

  async loadTestRunTest (testRunTestId: number): Promise<any> {
    const result = await API.get(`test-run-test/${testRunTestId}`, TestRunTest)
    return result.data
  }

  @action
  async updateStatus (testRunId: number, status: TestRunStatus): Promise<TestRun | undefined> {
    this.changingStatus = true
    const result = await API.patch(`/test-runs/${testRunId}`, { status }, TestRun)
    let testRun = result.data
    let maxRuns = 60

    while (testRun !== undefined && testRun.status !== status && maxRuns-- > 0) {
      await sleep(1000)
      testRun = (await API.get(`/test-runs/${testRunId}`, TestRun)).data
    }

    await this.loadTestRunTests(testRunId, false)

    runInAction(() => {
      this.changingStatus = false
      if (testRun !== undefined) this.storeTestRun(testRun)
    })
    return testRun
  }

  @action
  storeTestRun (testRun: TestRun): TestRun {
    const index = this.testRuns.findIndex((t) => t.id === testRun.id)
    if (index < 0) {
      this.testRuns.push(testRun)
    } else {
      this.testRuns[index] = testRun
    }
    return testRun
  }

  async loadDeploysForTestRun (testRunId: number): Promise<Deploy[]> {
    const result = await API.getList(`/test-runs/${testRunId}/deploys`, Deploy)
    return result.data ?? []
  }

  getStatusFilters (testRunId: number): TestStatus[] {
    return this.statusFiltersByTestRun[testRunId] ?? []
  }

  @action
  updateStatusFilters (testRunId: number, statuses: TestStatus[]): TestStatus[] {
    this.statusFiltersByTestRun[testRunId] = statuses
    return statuses
  }

  @action
  updateTestRunTab (testRunId: number, tab: string): void {
    this.testRunTabSetting[testRunId] = tab
    this.updateSelectedParentId(testRunId, null)
  }

  getTestRunTab (testRunId: number): string | undefined {
    return this.testRunTabSetting[testRunId]
  }

  @action
  updateSelectedParentId (testRunId: number, parentId: number | null): void {
    this.getTestRunTests(testRunId).forEach((t) => {
      if (t.parentId === parentId) {
        t.selected = true
      } else if (t.selected) {
        t.selected = false
      }
    })
  }

  @action
  updateOpenItems (testRunId: number, data: BooleanMap): void {
    this.openItemsMap[testRunId] = data
  }

  getOpenItems (testRunId: number): BooleanMap {
    return this.openItemsMap[testRunId] ?? {}
  }

  @action
  toggleTestRunTestOpen (testRunTestId: number): void {
    this.openTestRunTests[testRunTestId] = !this.openTestRunTests[testRunTestId]
  }

  @action
  clearTestRunTestOpen (): void {
    this.openTestRunTests = {}
  }

  @computed
  get eligibleForComparison (): TestRun[] {
    return chain(this.testRuns)
      .filter((p) => p.hasResults)
      .orderBy(['createdAt', 'id'], ['desc', 'asc'])
      .value()
  }

  isTestRunTestOpen (testRunTestId: number): boolean {
    return !!this.openTestRunTests[testRunTestId]
  }

  async loadMessages (testRunId: number): Promise<Message[]> {
    const result = await API.getList(`/test-runs/${testRunId}/messages`, Message)
    return runInAction(() => (this.testRunMessages[testRunId] = sortBy(result.data, 'createdAt').reverse()))
  }

  getMessages (testRunId: number): Message[] {
    return this.testRunMessages[testRunId] ?? []
  }

  async refreshMessages (testRunId: number): Promise<Message[]> {
    const messages = this.testRunMessages[testRunId] ?? []

    if (messages.length === 0) return await this.loadMessages(testRunId)

    const since = (parseServerDateTime(messages[0].createdAt)?.getTime() ?? 0) + 1
    const result = await API.getList(`/test-runs/${testRunId}/messages?since=${since}`, Message)
    return runInAction(
      () => (this.testRunMessages[testRunId] = [...sortBy(result.data, 'createdAt').reverse(), ...messages])
    )
  }

  async saveMessage (testRunId: number, text: string): Promise<Message | Message[] | undefined> {
    if (isBlank(text)) return this.testRunMessages[testRunId]

    const result = await API.post(`/test-runs/${testRunId}/messages`, { text }, Message)

    return runInAction(() => {
      if (!this.currentUserIsFollowingTestRun(testRunId)) {
        void this.loadFollowers(testRunId)
      }
      const messages = this.testRunMessages[testRunId] ?? []
      if (result.data !== undefined) {
        this.testRunMessages[testRunId] = [result.data, ...messages]
      }
      return result.data
    })
  }

  async loadTestListResult (testRunId: number): Promise<any> {
    const result = await API.get(`/test-runs/${testRunId}/test-list-result`, TestListResult)
    return result.data
  }

  async loadFollowers (testRunId: number): Promise<UserFollow[]> {
    const result = await API.getList(`/test-runs/${testRunId}/followers`, UserFollow)
    return runInAction(() => (this.testRunFollowers[testRunId] = result.data ?? []))
  }

  currentUserIsFollowingTestRun (testRunId: number): boolean {
    const userId = this.AppStore.user.id
    return this.getFollowers(testRunId).find((f) => f.userId === userId) !== undefined
  }

  getFollowers (testRunId: number): UserFollow[] {
    return this.testRunFollowers[testRunId] ?? []
  }

  async toggleFollow (testRunId: number): Promise<void> {
    await API.post('users/me/toggle-follow', { testRunId }, UserFollow)
    await this.loadFollowers(testRunId)
  }

  async getFirstTestRun (): Promise<TestRun | undefined> {
    const result = await API.get('/test-runs/first', TestRun)
    return result.data
  }
}
